import { useState, useEffect, useRef } from "react";

import { Logger } from "aws-amplify";
import { useCart } from "../../context/cart-context";
import { useNavigate } from "react-router-dom";
import { calculateTaxAmount, cleanupPhoneNumber, convertCentsToDollars, sendCustomEmail } from "../../util/util";
import { useMutation } from "@apollo/client";
import { CREATE_ORDER, CREATE_STRIPE_PAYMENT_INTENT, UPDATE_ORDER } from "../../graphql/customMutations";
import { EPromotionType, IGET_RESTAURANT_CATEGORY, IGET_RESTAURANT_PRODUCT } from "../../graphql/customQueries";
import { orderPlacedPath, restaurantListPath } from "../main";
import { ProductModal } from "../modals/product";
import { ICartProduct, IPreSelectedModifiers, ICartModifierGroup, EPaymentMethod, EOrderType, ICartModifier } from "../../model/model";
import { useUser } from "../../context/user-context";
import { PageWrapper } from "../../tabin/components/pageWrapper";
import { Button } from "../../tabin/components/button";
import { useRegister } from "../../context/register-context";
import { toLocalISOString } from "../../util/util";
import { useRestaurant } from "../../context/restaurant-context";
import { TextArea } from "../../tabin/components/textArea";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { Link } from "../../tabin/components/link";
import { Auth } from "./auth/auth";
import { CloseIcon } from "../../tabin/components/icons/closeIcon";
import { toast } from "../../tabin/components/toast";
import { FullScreenSpinner } from "../../tabin/components/fullScreenSpinner";
import { isMobile } from "react-device-detect";
import { OrderScheduleDateTime } from "../../tabin/components/orderScheduleDateTime";
import { useErrorLogging } from "../../context/errorLogging-context";
import { PromotionCodeModal } from "../modals/promotionCodeModal";
import { OrderThresholdMessageModal } from "../modals/orderThresholdMessageModal";

import "./checkout.scss";
import { ProductModifier } from "../shared/productModifier";
import { Radio } from "../../tabin/components/radio";
import { Stepper } from "../../tabin/components/stepper";
import { OrderTypeModal } from "../modals/orderTypeModal";

const logger = new Logger("checkout");

// Component
export const Checkout = () => {
    const navigate = useNavigate();

    // context
    const {
        orderType,
        products,
        notes,
        setNotes,
        tableNumber,
        clearCart,
        promotion,
        total,
        surcharge,
        subTotal,
        updateProduct,
        updateProductQuantity,
        deleteProduct,
        addProduct,
        userAppliedPromotionCode,
        removeUserAppliedPromotion,
        setIsShownUpSellCrossSellModal,
        isShownOrderThresholdMessageModal,
        setIsShownOrderThresholdMessageModal,
        orderScheduledAt,
        updateOrderScheduledAt,
        guestInformation,
        paymentMethod,
        setPaymentMethod,
        covers,
        setCovers,
        bugFixRecalculateTotal,
        bugFixRecalculateSubTotalAndSurcharge,
        deliveryAddress,
        deliveryFeeDiscount,
        deliveryFee,
        createDelivery,
    } = useCart();
    const { restaurant } = useRestaurant();
    const { user } = useUser();
    const { logError } = useErrorLogging();

    const [showSpinner, setShowSpinner] = useState(false);

    const stripe = useStripe();
    const elements = useElements();

    const [createStripePaymentIntentMutation] = useMutation(CREATE_STRIPE_PAYMENT_INTENT, {
        update: (proxy, mutationResult) => {},
    });

    const [createOrderMutation] = useMutation(CREATE_ORDER, {
        update: (proxy, mutationResult) => {
            logger.debug("mutation result: ", mutationResult);
        },
    });

    const [updateOrderMutation] = useMutation(UPDATE_ORDER, {
        update: (proxy, mutationResult) => {
            logger.debug("mutation result: ", mutationResult);
        },
    });

    // state
    const [productToEdit, setProductToEdit] = useState<{
        product: ICartProduct;
        displayOrder: number;
    } | null>(null);
    const [showEditProductModal, setShowEditProductModal] = useState(false);
    const [showPromotionCodeModal, setShowPromotionCodeModal] = useState(false);
    const [showOrderThresholdMessageModal, setShowOrderThresholdMessageModal] = useState(false);
    const [showOrderTypeModal, setShowOrderTypeModal] = useState(false);

    const authSectionRef = useRef<HTMLDivElement>(null);
    const orderTimeSectionRefionRef = useRef<HTMLDivElement>(null);

    // const [showItemUpdatedModal, setShowItemUpdatedModal] = useState(false);

    const { register } = useRegister();

    useEffect(() => {
        if (showEditProductModal) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "unset";
        }
    }, [showEditProductModal]);

    if (!restaurant) {
        navigate(restaurantListPath);
        return <div>Invalid restaurant</div>;
        // throw "Restaurant is invalid";
    }

    if (!register) {
        throw "Register is not valid";
    }

    // callbacks
    const onClosePromotionCodeModal = () => {
        setShowPromotionCodeModal(false);
    };

    const onCloseEditProductModal = () => {
        setProductToEdit(null);
        setShowEditProductModal(false);
    };

    const onCloseOrderThresholdMessageModal = () => {
        setShowOrderThresholdMessageModal(false);
    };

    const onCloseOrderTypeModal = () => {
        setShowOrderTypeModal(false);
    };

    // const onCloseItemUpdatedModal = () => {
    //     setShowItemUpdatedModal(false);
    // };

    const onEditProduct = (product: ICartProduct, displayOrder: number) => {
        setProductToEdit({ product, displayOrder });
        setShowEditProductModal(true);
    };

    const onUpdateProductQuantity = (displayOrder: number, productQuantity: number) => {
        updateProductQuantity(displayOrder, productQuantity);
    };

    const onRemoveProduct = (displayOrder: number) => {
        deleteProduct(displayOrder);
    };

    const onNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setNotes(e.target.value);
    };

    // submit callback
    const createOrder = async (bugFixTotal: number, bugFixSubTotal: number, bugFixSurcharge: number, stripePaymentId?: string) => {
        const now = new Date();

        // if (!user) {
        //     await logError(
        //         JSON.stringify({
        //             error: "Invalid user",
        //             context: { orderRestaurantId: restaurant.id },
        //         })
        //     );
        //     throw "Invalid user";
        // }
        if (!orderType) {
            await logError(
                "Invalid order type",
                JSON.stringify({
                    error: "Invalid order type",
                    context: { orderRestaurantId: restaurant.id, orderType: orderType },
                }),
            );
            throw "Invalid order type";
        }
        if (!restaurant) {
            await logError("Invalid restaurant", JSON.stringify({ restaurant: restaurant }));
            throw "Invalid restaurant";
        }
        if (!products || products.length == 0) {
            await logError(
                "No products have been selected",
                JSON.stringify({
                    error: "No products have been selected",
                    context: { orderRestaurantId: restaurant.id },
                }),
            );
            throw "No products have been selected";
        }

        const updatedProducts: ICartProduct[] = JSON.parse(JSON.stringify(products));

        const modifierToProductMapping: { [modifierId: string]: { productId: string; productName: string } } = {
            "9b250056-7ad4-452e-be0e-f6528b8cd888": {
                productId: "2576e931-396d-4cf0-8443-7e4d5bd08276_187",
                productName: "SPRING ROLLS (3)",
            },
            "8efc59e4-dae1-4ad5-8158-5972776fdab4": {
                productId: "66d109c5-f347-4b66-ad7e-ce32761f176b_188",
                productName: "RICE PAPER ROLLS (2)",
            },
            "50542f30-da76-4794-bb50-a336625fc689": {
                productId: "d4c24aaf-9367-410b-8212-7160cfeabbc5_189",
                productName: "CHICKEN SKEWERS (2)",
            },
            "708c7990-7d9c-4848-b0de-ea727ea882e5": {
                productId: "e2d1481c-057d-4284-89c1-441d85978dd4_190",
                productName: "GREEN RICE PRAWNS (3)",
            },
            "87716ec4-2014-4912-8d03-d5d1e94c6af9": {
                productId: "2fdc4487-5c1d-44a3-a14c-85190f2735bf_132",
                productName: "COKE",
            },
            "60d48a0d-8f95-4e62-a4a2-01d48b961291": {
                productId: "7fdc6c64-6d80-4e60-a40b-fe05ae94b262_134",
                productName: "COKE ZERO",
            },
            "04987870-e9d1-4978-85e0-010ffdba6729": {
                productId: "2e002067-c3f4-4e37-8f6d-5ef346d9aef5_133",
                productName: "SPRITE",
            },
            "97ed87a6-cd2b-4737-a52e-8463a2f6d6ac": {
                productId: "14e89ed9-3ca5-45fc-a528-5836ad46a2ff_58",
                productName: "ICED VIETNAMESE COFFEE",
            },
            "a4d189a4-dfea-4ed4-a087-2e7aae57d85b": {
                productId: "f8b80cc7-4541-4d53-b03d-7ea33e606a84_233",
                productName: "GREEN APPLE FRTY TEA",
            },
            "d7276767-d0df-4162-93ed-151dfcbbb061": {
                productId: "65594fa8-ffbf-41c7-a900-8321cd3cfec8_234",
                productName: "LYCHEE FRTY TEA",
            },
            "12cc2667-ea93-438d-ace7-30881bcc7fbf": {
                productId: "8aaf2b9f-8ea1-4703-a0a6-7a537c15563c_230",
                productName: "CREAMY MANGO TEA",
            },
            "b4436de6-bcca-46d2-9bec-4c88b197f580": {
                productId: "d306d550-77a6-4a14-a89a-ba984e3a6e08_231",
                productName: "TARO TEA",
            },
            "7575fd7b-cfbc-4a11-8744-1ec2cca9115b": {
                productId: "6c9513a2-ab5e-4a3e-ab0c-08620a4c96ba_229",
                productName: "BROWN SUGAR TEA",
            },
            "2fdda1f2-1535-4033-ba16-37ff51191ff4": {
                productId: "b1fb8472-ff23-412f-b7ae-66213e0631b2_228",
                productName: "ORIGINAL BUBBLE TEA",
            },
            "eb9b52d7-9707-4530-926e-59cb36fd025c": {
                productId: "707d9802-a3d0-4e89-b612-490750ab6c99_235",
                productName: "PASSION FRUIT TEA",
            },
        };

        const buildUpgradeProduct = (modifier: ICartModifier, match: { productId: string; productName: string }, parentQuantity: number): ICartProduct => ({
            id: match.productId,
            name: match.productName,
            kitchenName: null,
            image: null,
            notes: null,
            price: modifier.price,
            totalPrice: modifier.price,
            discount: 0,
            // isAgeRescricted: false,
            quantity: modifier.quantity * parentQuantity,
            category: null,
            modifierGroups: [],
        });

        const upgradeProducts: ICartProduct[] = [];

        updatedProducts.forEach((product) => {
            product.modifierGroups.forEach((modifierGroup) => {
                modifierGroup.modifiers.forEach((modifier) => {
                    const match = modifierToProductMapping[modifier.id];

                    if (match) {
                        // Top-level modifier match: add the mapped product, don't descend further.
                        upgradeProducts.push(buildUpgradeProduct(modifier, match, product.quantity));
                        return;
                    }

                    // Otherwise descend into nested product-modifier-modifier groups.
                    modifier.productModifiers?.forEach((productModifier) => {
                        productModifier.modifierGroups.forEach((nestedGroup) => {
                            nestedGroup.modifiers.forEach((nestedModifier) => {
                                const nestedMatch = modifierToProductMapping[nestedModifier.id];

                                if (nestedMatch) {
                                    upgradeProducts.push(buildUpgradeProduct(nestedModifier, nestedMatch, product.quantity));
                                }
                            });
                        });
                    });
                });
            });

            // No modifier groups should remain on any product in the order.
            product.modifierGroups = [];
        });

        updatedProducts.push(...upgradeProducts);

        let variables;
        try {
            variables = {
                country: restaurant.country,
                status: "NEW",
                paid: true,
                // cashPayment: false,
                onlineOrder: true,
                guestCheckout: true, //TODO: Update later when actual auth is added
                stripePaymentId: stripePaymentId,
                orderScheduledAt: orderScheduledAt,
                orderScheduledAtUtc: orderScheduledAt ? new Date(orderScheduledAt).toISOString() : null,
                customerInformation: {
                    firstName: guestInformation.firstName || "",
                    email: guestInformation.email || "",
                    phoneNumber: cleanupPhoneNumber(guestInformation.phoneNumber) || "",
                },
                type: orderType,
                number: tableNumber ? "QR" : "Web", //TODO: Remove later on
                covers: register && register.enableCovers ? covers : undefined,
                table: tableNumber,
                paymentAmounts: { cash: 0, eftpos: 0, online: stripePaymentId ? bugFixSubTotal : 0 },
                payments: stripePaymentId
                    ? {
                          type: "ONLINE",
                          amount: bugFixSubTotal,
                      }
                    : undefined,
                notes: notes,
                thirdPartyDeliveryId: "",
                deliveryAddress:
                    EOrderType.DELIVERY && deliveryAddress
                        ? `${deliveryAddress.street_address.join(", ")}, ${deliveryAddress.city}, ${deliveryAddress.state} ${deliveryAddress.zip_code}`
                        : "No address available",
                deliveryFeeDiscount: EOrderType.DELIVERY ? deliveryFeeDiscount : undefined,
                deliveryFee: EOrderType.DELIVERY ? deliveryFee : undefined,
                deliveryTrackingUrl: "",
                total: bugFixTotal,
                surcharge: bugFixSurcharge || undefined,
                discount: promotion ? promotion.discountedAmount : undefined,
                promotionId: promotion ? promotion.promotion.id : undefined,
                promotionType: promotion ? promotion.promotion.type : undefined,
                tax: Math.round(calculateTaxAmount(restaurant.country, bugFixSubTotal)),
                subTotal: bugFixSubTotal,
                registerId: register.id,
                products: updatedProducts, // copy obj so we can mutate it later
                placedAt: toLocalISOString(now),
                placedAtUtc: now.toISOString(),
                orderBatchable: register.orderBatchWindowInSeconds ? 1 : 0,
                orderBatchWindowInSeconds: register.orderBatchWindowInSeconds || undefined,
                orderUserId: restaurant.restaurantManagerId, //TODO: Update later when actual auth is added
                orderRestaurantId: restaurant.id,
            };
            if (restaurant.autoCompleteOrders) {
                variables.status = "COMPLETED";
                //@ts-ignore
                variables.completedAt = toLocalISOString(now);
                //@ts-ignore
                variables.completedAtUtc = now.toISOString();
                variables.paid = true;
            }
        } catch (e) {
            await logError(
                "Error in createOrderMutation input",
                JSON.stringify({
                    error: "No products have been selected",
                    context: {
                        country: restaurant.country,
                        status: "NEW",
                        paid: true,
                        // cashPayment: false,
                        onlineOrder: true,
                        guestCheckout: true, //TODO: Update later when actual auth is added
                        stripePaymentId: stripePaymentId,
                        orderScheduledAt: orderScheduledAt,
                        orderScheduledAtUtc: orderScheduledAt ? new Date(orderScheduledAt).toISOString() : null,
                        customerInformation: {
                            firstName: guestInformation.firstName || "",
                            email: guestInformation.email || "",
                            phoneNumber: cleanupPhoneNumber(guestInformation.phoneNumber) || "",
                        },
                        type: orderType,
                        number: tableNumber ? "QR" : "Web", //TODO: Remove later on
                        covers: register && register.enableCovers ? covers : undefined,
                        table: tableNumber,
                        notes: notes,
                        paymentAmounts: { cash: 0, eftpos: 0, online: bugFixSubTotal },
                        payments: {
                            type: "ONLINE",
                            amount: bugFixSubTotal,
                        },
                        deliveryAddress:
                            EOrderType.DELIVERY && deliveryAddress
                                ? `${deliveryAddress.street_address.join(", ")}, ${deliveryAddress.city}, ${deliveryAddress.state} ${deliveryAddress.zip_code}`
                                : "No address available",
                        deliveryFeeDiscount: EOrderType.DELIVERY ? deliveryFeeDiscount : undefined,
                        deliveryFee: EOrderType.DELIVERY ? deliveryFee : undefined,
                        total: bugFixTotal,
                        discount: promotion ? promotion.discountedAmount : undefined,
                        promotionId: promotion ? promotion.promotion.id : undefined,
                        promotionType: promotion ? promotion.promotion.type : undefined,
                        tax: Math.round(calculateTaxAmount(restaurant.country, bugFixSubTotal)),
                        subTotal: bugFixSubTotal,
                        surcharge: bugFixSurcharge || undefined,
                        registerId: register.id,
                        products: JSON.stringify(updatedProducts), // copy obj so we can mutate it later
                        placedAt: toLocalISOString(now),
                        placedAtUtc: now.toISOString(),
                        orderBatchable: register.orderBatchWindowInSeconds ? 1 : 0,
                        orderBatchWindowInSeconds: register.orderBatchWindowInSeconds || undefined,
                        orderUserId: restaurant.restaurantManagerId, //TODO: Update later when actual auth is added
                        orderRestaurantId: restaurant.id,
                    },
                }),
            );
            throw "Error in createOrderMutation input";
        }
        try {
            if (notes == null || notes == "") {
                //@ts-ignore
                delete variables.notes;
            }
            variables.products.forEach((product) => {
                if (product.modifierGroups.length == 0) {
                    //@ts-ignore
                    delete product.modifierGroups;
                }
                // if (product.image == null) {
                //     //@ts-ignore
                //     delete product.image;
                // }
                if (product.notes == null || product.notes == "") {
                    //@ts-ignore
                    delete product.notes;
                }
                // if (product.category.image == null) {
                //     //@ts-ignore
                //     delete product.category.image;
                // }
            });
            // process order
            const res = await createOrderMutation({
                variables: variables,
            });
            logger.debug("process order mutation result: ", res);

            return res.data.createOrder;
        } catch (e) {
            console.log("process order mutation error: ", e);

            await logError(e, JSON.stringify({ error: e, variables: variables }));
            throw e;
        }
    };

    const onCreateTransaction = async () => {
        // if (!user) {
        //     toast.error("You must log in before placing an order.");
        //     return;
        // }

        if (restaurant.orderThresholds?.enable && restaurant.orderThresholdMessage && !isShownOrderThresholdMessageModal) {
            setShowOrderThresholdMessageModal(true);
            return;
        }

        if (!paymentMethod) {
            toast.error("Please select a payment type");
            return;
        }

        if ((orderScheduledAt == "INVALID" || !orderScheduledAt) && !tableNumber) {
            orderTimeSectionRefionRef.current?.scrollIntoView({ behavior: "smooth" });
            toast.error("Please select a valid order time before placing an order.");
            return;
        }

        if (!guestInformation.email || guestInformation.emailError) {
            authSectionRef.current?.scrollIntoView({ behavior: "smooth" });
            toast.error("Please provide a valid email address before placing an order.");
            return;
        }

        if (
            tableNumber === null &&
            (!guestInformation.firstName || guestInformation.firstNameError || !guestInformation.phoneNumber || guestInformation.phoneNumberError)
        ) {
            authSectionRef.current?.scrollIntoView({ behavior: "smooth" });
            toast.error("Please fill out all customer information before placing an order.");
            return;
        }

        if (orderType === EOrderType.DELIVERY && !deliveryAddress) {
            setShowOrderTypeModal(true);
            toast.error("Please enter a valid delivery address.");
            return;
        }

        if (!stripe || !elements) {
            toast.error("Failed to configure Stripe correctly. Please try again later.");
            return;
        }

        if (!restaurant.stripeAccountId) {
            alert("This restaurant has not yet been setup with online ordering. Please contact a Tabin representative!");
            return;
        }

        try {
            setShowSpinner(true);

            const bugFixTotal = bugFixRecalculateTotal(products);
            const bugFixSubTotalandSurcharge = bugFixRecalculateSubTotalAndSurcharge(bugFixTotal);
            const bugFixSubTotal = bugFixSubTotalandSurcharge.subTotal;
            const bugFixSurcharge = bugFixSubTotalandSurcharge.surcharge;

            let deliveryTrackingUrl = "";

            if (paymentMethod === EPaymentMethod.EFTPOS) {
                const element = elements.getElement(CardElement);
                if (!element) return;

                const paymentMethodResult = await stripe.createPaymentMethod({
                    type: "card",
                    card: element,
                });

                if (paymentMethodResult.error) throw paymentMethodResult.error.message;

                const stripePaymentMethodId = paymentMethodResult.paymentMethod.id;

                const createStripePaymentIntentMutationResult: any = await createStripePaymentIntentMutation({
                    variables: {
                        amount: bugFixSubTotal,
                        restaurantId: restaurant.id,
                        stripePaymentMethodId: stripePaymentMethodId,
                        onlinePaymentGatewayProvider: "STRIPE",
                        orderType: orderType,
                        deliveryFee: deliveryFee,
                        deliveryFeeDiscount: deliveryFeeDiscount,
                    },
                });

                const clientSecret = createStripePaymentIntentMutationResult.data.createStripePaymentIntent.clientSecret;

                const cardPayment = await stripe.confirmCardPayment(clientSecret, {
                    payment_method: stripePaymentMethodId,
                });

                if (cardPayment.error) {
                    throw cardPayment.error.message;
                }

                const stripePaymentId = cardPayment.paymentIntent.id;
                console.log(`PaymentIntent (${stripePaymentId}): ${cardPayment.paymentIntent.status}`);

                const createOrderResult = await createOrder(bugFixTotal, bugFixSubTotal, bugFixSurcharge, stripePaymentId);

                if (orderType === EOrderType.DELIVERY) {
                    try {
                        const subject = `🏎️ New Delivery Order`;
                        const text = JSON.stringify({ restaurantName: restaurant.name, ...createOrderResult });
                        const html = `<div>${JSON.stringify({ restaurantName: restaurant.name, ...createOrderResult })}</div>`;

                        await sendCustomEmail({
                            to: "nipoon@tabin.co.nz;leo@tabin.co.nz",
                            from: {
                                name: "Tabin Dashboard",
                                email: "nipoon@tabin.co.nz",
                            },
                            subject,
                            text,
                            html,
                        });
                    } catch (e) {}

                    const deliveryResult = await createDelivery(createOrderResult.id);

                    console.log("xxx...deliveryResult", deliveryResult);

                    if (deliveryResult.error) {
                        toast.error(deliveryResult.error);
                        throw deliveryResult.error;
                    }

                    if (deliveryResult.deliveryResponse) {
                        let variables = {
                            orderId: createOrderResult.id,
                            thirdPartyDeliveryId: deliveryResult.deliveryResponse.id,
                            deliveryTrackingUrl: deliveryResult.deliveryResponse.tracking_url,
                        };
                        deliveryTrackingUrl = deliveryResult.deliveryResponse.tracking_url;

                        await updateOrderMutation({ variables });
                    }
                }
            } else {
                const createOrderResult = await createOrder(bugFixTotal, bugFixSubTotal, bugFixSurcharge);
                deliveryTrackingUrl = "Delivery not created as order has not been paid for.";
            }

            setShowSpinner(false);
            clearCart();
            navigate(`${orderPlacedPath}`, { state: { orderType, deliveryTrackingUrl, tableNumber, orderScheduledAt } });
        } catch (err) {
            console.log("Error in onCreateTransaction: ", err);
            alert(err);
            setShowSpinner(false);
        }
    };

    const orderSummary = (
        <OrderSummary
            onNotesChange={onNotesChange}
            onEditProduct={onEditProduct}
            onUpdateProductQuantity={onUpdateProductQuantity}
            onRemoveProduct={onRemoveProduct}
        />
    );

    const onUpdateProduct = (index: number, product: ICartProduct) => {
        updateProduct(index, product);
        // setShowItemUpdatedModal(true);
    };

    const editProductModal = () => {
        let category: IGET_RESTAURANT_CATEGORY | null = null;
        let product: IGET_RESTAURANT_PRODUCT | null = null;

        if (!productToEdit) {
            return <></>;
        }

        restaurant.categories.items.forEach((c) => {
            if (productToEdit.product.category && c.id == productToEdit.product.category.id) {
                category = c;
            }

            c.products &&
                c.products.items.forEach((p) => {
                    if (p.product.id == productToEdit.product.id) {
                        product = p.product;
                    }
                });
        });

        if (!product || !category) {
            return <></>;
        }

        let orderedModifiers: IPreSelectedModifiers = {};

        productToEdit.product.modifierGroups.forEach((mg) => {
            orderedModifiers[mg.id] = mg.modifiers;
        });

        console.log("orderedModifiers", orderedModifiers);

        return (
            <ProductModal
                category={category}
                product={product}
                isOpen={showEditProductModal}
                onClose={onCloseEditProductModal}
                onUpdateProduct={onUpdateProduct}
                editProduct={{
                    orderedModifiers: orderedModifiers,
                    quantity: productToEdit.product.quantity,
                    notes: productToEdit.product.notes,
                    productCartIndex: productToEdit.displayOrder,
                }}
            />
        );
    };

    const promotionCodeModal = () => {
        return <>{showPromotionCodeModal && <PromotionCodeModal isOpen={showPromotionCodeModal} onClose={onClosePromotionCodeModal} />}</>;
    };

    const thresholdMessageModal = () => {
        return (
            <>
                {showOrderThresholdMessageModal && (
                    <OrderThresholdMessageModal
                        isOpen={showOrderThresholdMessageModal}
                        onClose={onCloseOrderThresholdMessageModal}
                        onContinue={() => setIsShownOrderThresholdMessageModal(true)}
                    />
                )}
            </>
        );
    };

    const orderTypeModal = <>{showOrderTypeModal && <OrderTypeModal isOpen={showOrderTypeModal} onClose={onCloseOrderTypeModal} />}</>;

    // const itemUpdatedModal = (
    //     <>
    //         {showItemUpdatedModal && <ItemAddedUpdatedModal isOpen={showItemUpdatedModal} onClose={onCloseItemUpdatedModal} isProductUpdate={true} />}
    //     </>
    // );

    const modalsAndSpinners = (
        <>
            {editProductModal()}
            {promotionCodeModal()}
            {thresholdMessageModal()}
            {orderTypeModal}
            {/* {itemUpdatedModal} */}
        </>
    );

    const cartEmptyDisplay = (
        <>
            <div className="cart-empty">
                <div>No items selected!</div>
                <div className="separator-2"></div>
            </div>
        </>
    );

    const title = (
        <div className="your-order-title mb-3">
            {/* <CachedImage className="image mr-2" url={`${getPublicCloudFrontDomainName()}/images/shopping-bag-icon.png`} alt="shopping-bag-icon" /> */}
            <div className="h2">Your Order</div>

            {isMobile && (
                <span
                    onClick={() => {
                        navigate(-1);
                    }}
                    className="pt-1 pb-1 pl-1 pr-1"
                >
                    <CloseIcon height={"14px"} className="checkout-close-button" />
                </span>
            )}
        </div>
    );

    const onUpdateCoversNumber = (count: number) => {
        setCovers(count);
    };

    const CoversInput = () => (
        <>
            <div className="mt-3 mb-6">
                <div className="h3 text-bold">Number Of Diners</div>
                <div className="separator-2"></div>
                <div>
                    <Stepper count={covers} min={1} max={20} onUpdate={onUpdateCoversNumber} size={32} />
                </div>
            </div>
        </>
    );

    const orderScheduleTitle = (
        <div ref={orderTimeSectionRefionRef} className="title mb-1 mt-6">
            <div className="h3">Order Time</div>
            <div className="separator-2"></div>
        </div>
    );

    const orderTypeTitle = (
        <div className="title mb-1 mt-6">
            <div className="h3">Order Type</div>
            <div className="separator-2"></div>
        </div>
    );

    const onChangeScheduleDateTime = (dateTimeLocalISO: string | null) => {
        updateOrderScheduledAt(dateTimeLocalISO);
    };

    const orderTitle = (
        <div className="title mb-1 mt-3">
            <div className="h3">Items</div>
            <div className="separator-2"></div>
        </div>
    );

    const restaurantNotes = (
        <>
            <div className="h3 mb-1 mt-6">Special Instructions</div>
            <div className="separator-2"></div>
            <TextArea placeholder={"Leave a note for the restaurant"} value={notes} onChange={onNotesChange} />
        </>
    );

    const order = (
        <>
            {orderSummary}
            {/* {restaurantNotes} */}
        </>
    );

    const paymentTitle = (
        <div className="title mb-1 mt-6">
            <div className="h3">Payment</div>
            <div className="separator-2"></div>
        </div>
    );

    const orderButton = (
        <div>
            <Button className="complete-order-button mt-2" onClick={onCreateTransaction} disabled={products ? products.length == 0 : true}>
                Order
            </Button>
        </div>
    );

    const onClickApplyPromotionCode = async () => {
        setShowPromotionCodeModal(true);
    };

    const onRemoveUserAppliedPromotionCode = () => {
        removeUserAppliedPromotion();
    };

    const promotionInformation = (
        <>
            {promotion && (
                <div className="checkout-promotion-information mb-2 mt-2 mb-2 pt-2 pr-2 pb-2 pl-2">
                    <div>
                        <div className="checkout-promotion-information-heading h5 mb-1">
                            <div>Promotion Applied!</div>
                            <div>-${convertCentsToDollars(promotion.discountedAmount)}</div>
                        </div>
                        {promotion.promotion.type !== EPromotionType.ENTIREORDER ? (
                            <div className="checkout-promotion-information-items">
                                {promotion.promotion.name}:{" "}
                                {Object.values(promotion.matchingProducts).map((p, index) => (
                                    <>
                                        {index !== 0 && ", "}
                                        {p.name}
                                    </>
                                ))}
                            </div>
                        ) : (
                            <div className="checkout-promotion-information-items">Entire Order</div>
                        )}
                    </div>

                    {userAppliedPromotionCode && (
                        <div className="checkout-remove-promotion" onClick={onRemoveUserAppliedPromotionCode}>
                            <CloseIcon height="12px" />
                        </div>
                    )}
                </div>
            )}
        </>
    );

    return (
        <>
            <PageWrapper>
                {showSpinner && <FullScreenSpinner show={showSpinner} text="Processing your order..." />}
                <div className="checkout">
                    <div className="order-wrapper">
                        {title}

                        {/* {tableNumber === null && ( */}
                        <div ref={authSectionRef} className="mb-6">
                            <Auth />
                        </div>
                        {/* )} */}

                        {register?.enableCovers && <CoversInput />}

                        {tableNumber === null && (
                            <div>
                                {orderScheduleTitle}
                                <OrderScheduleDateTime
                                    onChange={onChangeScheduleDateTime}
                                    operatingHours={restaurant.operatingHours}
                                    preparationTimeInMinutes={restaurant.preparationTimeInMinutes || 10}
                                />
                            </div>
                        )}

                        <div>
                            {orderTypeTitle}

                            <div className="text-bold mb-3">
                                {orderType} {orderType === EOrderType.DINEIN ? `(Table ${tableNumber})` : ``}{" "}
                                {restaurant.enableOnlineDelivery && <Link onClick={() => setShowOrderTypeModal(true)}>Change</Link>}
                            </div>
                            {orderType === EOrderType.PICKUP ? (
                                <div className="mb-1">{restaurant.name}</div>
                            ) : orderType === EOrderType.DELIVERY ? (
                                <div className="mb-1">Your Delivery Address:</div>
                            ) : null}

                            {orderType === EOrderType.PICKUP ? (
                                <div className="restaurant-address">{restaurant.address.formattedAddress}</div>
                            ) : orderType === EOrderType.DELIVERY ? (
                                <div className="restaurant-address">
                                    {deliveryAddress ? (
                                        <>
                                            <div>{deliveryAddress.street_address.join(", ")}</div>
                                            <div>
                                                {deliveryAddress.city}, {deliveryAddress.state} {deliveryAddress.zip_code}
                                            </div>
                                        </>
                                    ) : (
                                        "No address available"
                                    )}
                                </div>
                            ) : null}
                        </div>

                        {orderTitle}
                        {promotionInformation}
                        <div className="order">{products && products.length > 0 ? order : cartEmptyDisplay}</div>
                        {/* 
                        {promotion && (
                            <div className="mb-2">
                                {`Discount: -$${convertCentsToDollars(promotion.discountedAmount)}`}{" "}
                                {userAppliedPromotionCode && <Link onClick={onRemoveUserAppliedPromotionCode}>Remove</Link>}
                            </div>
                        )} */}

                        {promotion && (
                            <div className="checkout-discount-wrapper mb-2 mt-1">
                                <div>{`Discount: (${promotion.promotion.code})`}</div>
                                <div>-${convertCentsToDollars(promotion.discountedAmount)}</div>
                            </div>
                        )}

                        {surcharge ? (
                            <div className="checkout-discount-wrapper mb-2 mt-1">
                                <div>Surcharge:</div>
                                <div>${convertCentsToDollars(surcharge)}</div>
                            </div>
                        ) : (
                            <></>
                        )}

                        {deliveryFee || deliveryFeeDiscount ? (
                            <div className="checkout-discount-wrapper mb-2 mt-1">
                                <div>Delivery Fee:</div>
                                <div>{deliveryFee === 0 && deliveryFeeDiscount > 0 ? "FREE" : `$${convertCentsToDollars(deliveryFee)}`}</div>
                            </div>
                        ) : null}

                        <div className="checkout-total-wrapper h4">
                            <div>Total:</div>
                            <div>${convertCentsToDollars(subTotal)}</div>
                        </div>

                        {restaurantNotes}

                        {paymentTitle}
                        <div className="mb-3">
                            <Radio
                                className="pay-by-card mb-2"
                                selected={paymentMethod === EPaymentMethod.EFTPOS}
                                onSelect={() => {
                                    setPaymentMethod(EPaymentMethod.EFTPOS);
                                }}
                            >
                                Pay By Card
                            </Radio>

                            {register.enablePayLater && (
                                <Radio
                                    className="pay-at-counter"
                                    selected={paymentMethod === EPaymentMethod.LATER}
                                    onSelect={() => {
                                        setPaymentMethod(EPaymentMethod.LATER);
                                    }}
                                >
                                    Pay Later At Counter
                                </Radio>
                            )}
                        </div>

                        {paymentMethod === EPaymentMethod.EFTPOS && (
                            <CardElement
                                className="mb-4 stripe-card-element"
                                options={{
                                    hidePostalCode: true,
                                    style: {
                                        base: {
                                            fontSize: "16px",
                                            color: "black",
                                            "::placeholder": {
                                                color: "#aaaaaa",
                                            },
                                        },
                                        invalid: {
                                            color: "#9e2146",
                                        },
                                    },
                                }}
                            />
                        )}

                        <div className="pay-later-link text-center mt-4 mb-3">
                            <Link onClick={onClickApplyPromotionCode}>Apply promo code</Link>
                        </div>
                        <div>{orderButton}</div>
                        <div className="text-center mt-3">
                            <a href="https://www.tabin.co.nz" target="_blank">
                                Powered by tabin.co.nz
                            </a>
                        </div>
                    </div>
                </div>
                {modalsAndSpinners}
            </PageWrapper>
        </>
    );
};

const OrderSummary = (props: {
    onEditProduct: (product: ICartProduct, displayOrder: number) => void;
    onRemoveProduct: (displayOrder: number) => void;
    onUpdateProductQuantity: (displayOrder: number, productQuantity: number) => void;
    onNotesChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
}) => {
    // context
    const { products } = useCart();

    // displays
    if (!products || products.length === 0) return <h1>No items in cart!</h1>;

    const orderItems = (
        <>
            {products &&
                products.map((product, index) => {
                    // using index as key because products can be duplicated
                    if (product) {
                        return (
                            <div key={index}>
                                <OrderItem
                                    product={product}
                                    displayOrder={index}
                                    onEditProduct={props.onEditProduct}
                                    onUpdateProductQuantity={props.onUpdateProductQuantity}
                                    onRemoveProduct={props.onRemoveProduct}
                                />
                                <div className="separator-2"></div>
                            </div>
                        );
                    }
                })}
        </>
    );

    return <>{orderItems}</>;
};

const OrderItem = (props: {
    product: ICartProduct;
    displayOrder: number;
    onEditProduct: (product: ICartProduct, displayOrder: number) => void;
    onUpdateProductQuantity: (displayOrder: number, productQuantity: number) => void;
    onRemoveProduct: (displayOrder: number) => void;
}) => {
    // constants
    let itemPrice = props.product.price * props.product.quantity;

    props.product.modifierGroups.forEach((mg) => {
        mg.modifiers.forEach((m) => {
            const changedQuantity = m.quantity - m.preSelectedQuantity;

            if (changedQuantity > 0) {
                itemPrice += m.price * changedQuantity * props.product.quantity;
            }
        });
    });

    // displays
    // const quantity = (
    //     <Stepper
    //         count={props.product.quantity}
    //         min={1}
    //         onUpdate={(count: number) => props.onUpdateProductQuantity(props.displayOrder, count)}
    //         size={32}
    //     />
    // );

    return (
        <>
            <div className="order-item">
                {/* {quantity}  */}
                <OrderItemDetails
                    quantity={props.product.quantity}
                    name={props.product.name}
                    price={props.product.price}
                    notes={props.product.notes}
                    modifierGroups={props.product.modifierGroups}
                    // onEditProduct={() => props.onEditProduct(props.product, props.displayOrder)}
                />
                {/* <div className="text-center">
                    <div className="h3 text-primary mb-2">${convertCentsToDollars(itemPrice)}</div>
                    <Button className="remove-button" onClick={() => props.onRemoveProduct(props.displayOrder)}>
                        Remove
                    </Button>
                </div> */}
                {!props.product.isPreSelectedProduct && (
                    <>
                        <Link className="edit-link mt-2 mr-2" onClick={() => props.onEditProduct(props.product, props.displayOrder)}>
                            Edit
                        </Link>
                        <Link className="remove-link mt-2" onClick={() => props.onRemoveProduct(props.displayOrder)}>
                            Remove
                        </Link>
                    </>
                )}
            </div>
        </>
    );
};

const OrderItemDetails = (props: {
    name: string;
    quantity: number;
    price: number;
    notes: string | null;
    modifierGroups: ICartModifierGroup[];
    // onEditProduct: () => void;
}) => {
    const { name, quantity, price, notes, modifierGroups } = props;
    const { isPOS } = useRegister();

    const modifierString = (preSelectedQuantity: number, quantity: number, name: string, price: number) => {
        const changedQuantity = quantity - preSelectedQuantity;
        let mStr = "";

        if (changedQuantity < 0 && Math.abs(changedQuantity) == preSelectedQuantity) {
            mStr = `(REMOVE) ${changedQuantity > 1 ? `${Math.abs(changedQuantity)} x ` : ""}${name}`;
        } else {
            mStr = `${quantity > 1 ? `${Math.abs(quantity)} x ` : ""}${name}`;
        }

        if (price > 0 && changedQuantity > 0) {
            mStr += ` (+$${convertCentsToDollars(price)})`;
        }

        return mStr;
    };

    const nameDisplayString = `${quantity > 1 ? `${quantity} x ` : ""}${name} ($${convertCentsToDollars(price)})`;

    const nameDisplay = (
        <div className="name-edit-button">
            <div className="h4">{nameDisplayString}</div>
        </div>
    );

    const modifiersDisplay = (
        <>
            {modifierGroups.map((mg, index) => (
                <>
                    {!mg.hideForCustomer && (
                        <>
                            <div className="h6 text-bold mt-1" key={mg.id}>
                                {mg.name}
                            </div>
                            {mg.modifiers.map((m) => (
                                <>
                                    <div key={m.id} className="h6 modifier-name">
                                        {modifierString(m.preSelectedQuantity, m.quantity, m.name, m.price)}
                                    </div>
                                    {m.productModifiers && (
                                        <div className="mb-2">
                                            {m.productModifiers.map((productModifier, index) => (
                                                <div>
                                                    <div className="mt-2"></div>
                                                    <ProductModifier
                                                        selectionIndex={m.productModifiers && m.productModifiers.length > 1 ? index + 1 : undefined}
                                                        product={productModifier}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </>
                            ))}
                        </>
                    )}
                </>
            ))}
        </>
    );

    const notesDisplay = <>{notes && <div className="text-grey mt-2">Notes: {notes}</div>}</>;

    return (
        <div className="detail">
            {nameDisplay}
            {modifiersDisplay}
            {notesDisplay}
        </div>
    );
};
